import { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import CardCQW from "../../../CardCQW/component";
import Grid from "../../../../stories/Components/Grid";
import Card from "../../../../stories/Components/Card";
import { CardVariantsEnum } from "../../../../stories/Components/Card/interface";
import VideoModal from "../../../VideoModal";
import ShowMoreAd from "./components/ShowMoreAd/component";
import { useLocation } from "react-router-dom";
import { setPageScrollPosition } from "../../../../utils/scroll/library";
/**
 * @param {Object}
 * columns: the number of columns in the layout, 4-5
 * variant: the card variant to use for the card grid
 * contentItems: the contentItems to be displayed as cards
 * @returns {JSX.Element} The CardGrid layout component
 */

const CardGrid = ({
    layout = {},
    items = [],
    featureAttributionData = {},
    cardClickHandler = () => {},
    indexToFocus = null,
    startAt = 1,
}) => {
    const { columns = 5, isFlexible = true } = layout;
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const [mediaItems, setMediaItems] = useState([]);
    const [playingVideoIndex, setVideoPlayingIndex] = useState(0);

    const playVideoInModal = layout?.videoPlayback === "open-modal";
    const { pathname } = useLocation();

    const handleClick = useCallback(() => {
        setPageScrollPosition(pathname);
    }, [pathname]);

    useEffect(() => {
        if (!playVideoInModal) return;

        items
            .filter((_, index) => index < items.length)
            .map((item, index) => {
                if (item.itemType === "video") {
                    const mediaItem = {
                        ...item,
                        featureAttributionPosition: startAt + index,
                    };
                    setMediaItems((prev) => [...prev, mediaItem]);
                }
                return item;
            });
    }, [items]);

    if (!layout.columns || !items.length) return;

    const getFeatureAttribution = (
        index,
        featureAttributionData,
        columns,
        totalItems
    ) => {
        const featureAttribution = {
            name: totalItems > columns ? "card-grid" : "swimlane",
            position: startAt + index, // using startAt helps get the correct position in the swimlane when the swimlane is broken into multiple CQWs
        };
        if (featureAttributionData?.instance)
            featureAttribution.instance = featureAttributionData.instance;
        return featureAttribution;
    };

    // Check if the card variant is available in Storybook, use that if it is else use CardCQW
    const CardComponent = Object.values(CardVariantsEnum).includes(
        layout.variant
    )
        ? Card
        : CardCQW;

    const handleClickVideo = (item) => () => {
        const index = mediaItems.findIndex(
            (mediaItem) => mediaItem.id === item.id
        );
        setVideoPlayingIndex(index);
        setModalIsOpen(true);
    };

    const handleClose = () => {
        setModalIsOpen(false);
    };

    const multiToOneColMobile =
        layout.variant === CardVariantsEnum.VERTICAL_TO_HORIZONTAL || false;

    return (
        <>
            <Grid
                columns={columns}
                isFlexible={isFlexible && !multiToOneColMobile}
                multiToOneColMobile={multiToOneColMobile}
            >
                {items
                    .filter((_, index) => index < items.length)
                    .map((item, index) => {
                        if (item.isShowMoreAd === true) {
                            return (
                                <ShowMoreAd
                                    key={`cqw-ad-${index}`}
                                    type={item.adType}
                                />
                            );
                        }

                        return (
                            <CardComponent
                                key={item.id + "-" + index}
                                {...item}
                                item={item}
                                focus={index === indexToFocus}
                                featureAttribution={getFeatureAttribution(
                                    index,
                                    featureAttributionData,
                                    layout.columns,
                                    items.length
                                )}
                                onClick={
                                    mediaItems.length > 0 &&
                                    item.itemType === "video"
                                        ? handleClickVideo(item)
                                        : handleClick
                                }
                                clickHandler={cardClickHandler}
                                variant={item.variant || layout.variant}
                                columns={columns}
                            />
                        );
                    })}
            </Grid>
            {mediaItems.length > 0 && (
                <VideoModal
                    isOpen={modalIsOpen}
                    handleClose={handleClose}
                    items={[mediaItems[playingVideoIndex]]}
                    playingVideoIndex={0}
                    featureAttributionName="card-grid"
                    aspectRatio="16:9"
                />
            )}
        </>
    );
};

CardGrid.propTypes = {
    layout: PropTypes.shape({
        columns: PropTypes.number,
        rows: PropTypes.number,
        isFlexible: PropTypes.bool,
    }),
    items: PropTypes.arrayOf(PropTypes.object),
    indexToFocus: PropTypes.number,
    featureAttributionData: PropTypes.shape({
        instance: PropTypes.string,
    }),
    cardClickHandler: PropTypes.func,
};

export default CardGrid;
